/**
 * MarginTotals — the Margin surface's header block: coverage band, the four cards, and the
 * one line that says what the totals cover. Shared by both tabs, which is why `span` is a
 * prop: the trend's totals cover the whole window, the breakdown's cover the node you are
 * standing on, and a card row with no period on it is the defect this replaced.
 *
 * ⭐ THE CARDS COME FIRST AND THE BAND EXPLAINS THEM (24 Aug 2026). The band used to sit
 * ABOVE the strip, holding the one number that explained why Value − Cost did not equal Gross
 * Margin — £2,847,905 − £1,884,814 = £963,091 against a card reading £867,136. A reader
 * subtracts the row in front of them before reading anything above it, so the band was
 * repairing the strip rather than explaining it. It is now collapsed BENEATH, and what it
 * holds is the two differences a reader actually meets: the uncosted lines, and the ledger.
 *
 * ⚠ The strip itself is unchanged — Value Generated · Cost of Production · Gross Margin (£) ·
 * Gross Margin (%). An earlier attempt made the held-out amount a fourth card so the row
 * subtracted; that put the same number in two places and lost GM% its own card. The strip is
 * the headline and stays the headline; the reconciliation belongs in the expansion.
 *
 * ⭐ AND THE CLOSED-PERIOD LINE SITS WITH THEM (23 Aug 2026). The server serves closed months
 * only, so the period the cards cover is NOT the period the scope bar asked for whenever the
 * window reaches into the month being billed now. That gap is exactly how the surface came to
 * show £4,527,388 of Group "2026" against a Sage year that stops at July, so the excluded
 * month is named here, beside the totals it changes — not on a chart the reader reaches after
 * they have already read the number.
 *
 * ⭐ THERE IS NO BASIS PROP ANY MORE (24 Aug 2026). This block used to carry the cost lens
 * down to the band and the cards, and a fallback note for when the chosen lens priced nothing
 * at this scope. The fact serves one cost, so the lens, the fallback and the note are all
 * gone; what the cost IS gets said instead, on the card itself (marginCoverage.costNote).
 *
 * Split out of MarginExplorer when the band pushed that file past its component budget. The
 * parts move together and are read together, so they are one file.
 */

import React from 'react'
import OrderBookTiles, { type SageSubs } from './OrderBookTiles'
import MarginCoverage from './MarginCoverage'
import { coverage, costNote, intercompany, tiles, type Intercompany } from '../viewmodels/marginCoverage'
import { MARGIN_COPY } from '../viewmodels/marginFact'
import { formatCurrency } from '../utils/formatters'
import type { SageWindow } from '../viewmodels/marginSage'
import type { MarginTotals as Totals } from '../types/margin'
import type { Site } from '../types/metrics'

/** The Group double-count, stated beside the Group figure rather than in a spec.
 *
 * ⚠ IT IS NOT COLLAPSED and it is not in the band below. A reader who takes £4,115,592 as
 * Group revenue has been misled by the time they open anything, so this sits in the same eye
 * line as the card that says it. Every figure here is served — see viewmodels/marginCoverage
 * .intercompany for why the platform names the amount and refuses to net it off. */
function Intercompany({ trade }: { trade: Intercompany }): React.JSX.Element {
  return (
    <p className="rounded-lg border border-line2 bg-warn-bg px-3 py-2 text-[11.5px] leading-relaxed text-mut">
      <span className="font-semibold text-amber">
        One Group site sold {formatCurrency(trade.value, '£', 0)} of this to the other
      </span>{' '}
      — {trade.pct.toFixed(1)}% of the value above, on {trade.lines.toLocaleString('en-GB')} billed
      lines. Sage does not eliminate the trade, and this platform does not either, so Group revenue
      counts it twice. The buyer&apos;s cost carries the other half, and only for the work it has
      re-sold. Elimination is a decision for Finance.
    </p>
  )
}

export default function MarginTotals({ totals, span, erp, site, sage, ledger }: {
  totals: Totals | null
  span: string | null
  /** The system the billed value came from, for the explanation band. */
  erp: string
  /** The scope, and the ONLY reason it is here: the intercompany trade is a double-count at
   * Group and plain revenue at an entity. See marginCoverage.intercompany. */
  site: Site
  /** The ledger figure beside each card. Always present on this surface — its PRESENCE is
   * what tells the strip it answers to a ledger, which is what drops the per-card notes. Its
   * fields are empty when the two windows are not the same one (viewmodels/marginSage). */
  sage: SageSubs
  /** The ledger's raw figures, for the reconciliation table inside the band. Named `ledger`
   * and not `sageWindow` — that is the FUNCTION that builds it, and one name for both is how
   * a prop ends up shadowing an import. */
  ledger: SageWindow
}): React.JSX.Element | null {
  if (!totals) return null
  const cover = coverage(totals)
  const strip = tiles(totals)
  const trade = intercompany(totals, site)
  return (
    <div className="space-y-2.5 px-4 pt-3.5 pb-4">
      <OrderBookTiles tiles={strip} costNote={costNote(totals)} sage={sage} />
      {trade && <Intercompany trade={trade} />}
      {cover && <MarginCoverage coverage={cover} erp={erp} tiles={strip} sage={ledger} />}
      <p className="font-mono text-[10px] text-mut2">
        {MARGIN_COPY.note}
        {span && ` · invoiced ${span}`}
      </p>
    </div>
  )
}
